import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { Search, ShieldPlus, ShieldMinus, RefreshCw } from "lucide-react";

export function UsersTab({ selfId, roleFilterLock }) {
  const [q, setQ] = useState("");
  const [role, setRole] = useState(roleFilterLock || "all");
  const [persona, setPersona] = useState("all");
  const [sort, setSort] = useState("recent");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ users: [], total: 0, page: 1, page_size: 25 });
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: 25, sort };
      if (q) params.q = q;
      if (role !== "all") params.role = role;
      if (persona !== "all") params.persona = persona;
      const { data } = await api.get("/admin/users", { params });
      setData(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to load users");
    } finally { setLoading(false); }
  }, [q, role, persona, sort, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [q, role, persona, sort]);

  const setRoleFor = async (u, newRole) => {
    try {
      await api.patch(`/admin/users/${u.id}/role`, { role: newRole });
      toast.success(newRole === "admin" ? `${u.name} is now an admin` : `${u.name} is no longer an admin`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Action failed");
    }
  };

  const totalPages = Math.max(1, Math.ceil(data.total / data.page_size));

  return (
    <div className="space-y-4" data-testid="admin-users">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input data-testid="admin-user-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or email…" className="pl-9 h-11 rounded-xl" />
        </div>
        {!roleFilterLock && (
          <Select value={role} onValueChange={setRole}>
            <SelectTrigger className="w-36 h-11 rounded-xl" data-testid="admin-filter-role"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All roles</SelectItem>
              <SelectItem value="user">Users</SelectItem>
              <SelectItem value="admin">Admins</SelectItem>
            </SelectContent>
          </Select>
        )}
        <Select value={persona} onValueChange={setPersona}>
          <SelectTrigger className="w-40 h-11 rounded-xl" data-testid="admin-filter-persona"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All levels</SelectItem>
            <SelectItem value="undergraduate">Undergraduate</SelectItem>
            <SelectItem value="postgraduate">Postgraduate</SelectItem>
          </SelectContent>
        </Select>
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger className="w-36 h-11 rounded-xl" data-testid="admin-filter-sort"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="recent">Newest</SelectItem>
            <SelectItem value="oldest">Oldest</SelectItem>
            <SelectItem value="name">Name A-Z</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card className="rounded-2xl border-border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Level</TableHead>
              <TableHead>Country</TableHead>
              <TableHead className="text-right">AI msgs</TableHead>
              <TableHead>Joined</TableHead>
              <TableHead>Role</TableHead>
              {roleFilterLock && <TableHead className="text-right">Action</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.users.map((u) => (
              <TableRow key={u.id} data-testid={`admin-user-row-${u.id}`}>
                <TableCell className="font-semibold text-navy">{u.name}</TableCell>
                <TableCell className="text-muted-foreground">{u.email}</TableCell>
                <TableCell className="capitalize">{u.persona}</TableCell>
                <TableCell>{u.country || u.target_country || "—"}</TableCell>
                <TableCell className="text-right tabular-nums">{u.ai_messages}</TableCell>
                <TableCell className="text-muted-foreground text-xs">{(u.created_at || "").slice(0, 10)}</TableCell>
                <TableCell>
                  {u.role === "admin"
                    ? <Badge className="bg-orange text-white hover:bg-orange">Admin</Badge>
                    : <Badge variant="secondary">User</Badge>}
                </TableCell>
                {roleFilterLock && (
                  <TableCell className="text-right">
                    {u.id === selfId ? (
                      <span className="text-xs text-muted-foreground">You</span>
                    ) : (
                      <Button size="sm" variant="outline" className="rounded-full" data-testid={`admin-demote-${u.id}`} onClick={() => setRoleFor(u, "user")}>
                        <ShieldMinus className="h-3.5 w-3.5 mr-1" /> Remove admin
                      </Button>
                    )}
                  </TableCell>
                )}
              </TableRow>
            ))}
            {data.users.length === 0 && (
              <TableRow><TableCell colSpan={roleFilterLock ? 8 : 7} className="text-center text-muted-foreground py-10">{loading ? "Loading…" : "No accounts found."}</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground">{data.total} account{data.total !== 1 ? "s" : ""}</span>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" className="rounded-full" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="admin-prev-page">Previous</Button>
          <span className="text-sm text-navy">Page {data.page} / {totalPages}</span>
          <Button size="sm" variant="outline" className="rounded-full" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} data-testid="admin-next-page">Next</Button>
        </div>
      </div>
    </div>
  );
}

export function AdminsTab({ selfId }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const addAdmin = async () => {
    if (!email) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/admins", { email });
      toast.success(`${data.name || data.email} is now an admin`);
      setEmail("");
      setReloadKey((k) => k + 1);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not add admin");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-5" data-testid="admin-admins">
      <Card className="p-5 rounded-2xl border-border">
        <h3 className="font-display font-extrabold italic text-navy mb-1">Add an administrator</h3>
        <p className="text-sm text-muted-foreground mb-4">Promote an existing MyElite account to admin by email.</p>
        <div className="flex flex-wrap items-center gap-3">
          <Input data-testid="admin-add-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="person@example.com" className="flex-1 min-w-[220px] h-11 rounded-xl" />
          <Button data-testid="admin-add-submit" onClick={addAdmin} disabled={busy} className="h-11 rounded-full bg-orange hover:bg-orange-dark text-white font-display font-bold italic uppercase tracking-wide">
            {busy ? <RefreshCw className="h-4 w-4 animate-spin" /> : <><ShieldPlus className="h-4 w-4 mr-1.5" /> Add admin</>}
          </Button>
        </div>
      </Card>
      <UsersTab key={reloadKey} selfId={selfId} roleFilterLock="admin" />
    </div>
  );
}
